import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Tooltip,
  Divider,
  Chip,
} from "@mui/material";
import { Icon } from "@iconify/react";
import dayjs from "dayjs";
import {
  useGetEventQuery,
  useRemoveAttendeesMutation,
  useGraduateAttendeesMutation,
} from "../../features/events/eventsApiSlice";
import { useNavigate, useParams } from "react-router-dom";
import TestingAttendeesPrintButton from "./TestingAttendeesPrintButton";
import { gradArray } from "../form/fields/inputFields";

const gradLabel = (degree) =>
  gradArray.find((g) => g.value === Number(degree))?.label ||
  String(degree ?? "");

const getStandortName = (att) => {
  if (!att) return "";
  const sr = att.standortRef;
  if (!sr) return "";
  if (typeof sr === "object") return sr?.siteName || "";
  return "";
};

const getAge = (att) => {
  if (typeof att?.age === "number") return att.age;
  if (!att?.birthdate) return "";
  const b = new Date(att.birthdate);
  const diff = Date.now() - b.getTime();
  const ageDt = new Date(diff);
  return Math.abs(ageDt.getUTCFullYear() - 1970);
};

const cols = [
  { id: "index", label: "#", sortable: false, width: 80 },
  { id: "firstName", label: "Vorname", sortable: true },
  { id: "lastName", label: "Nachname", sortable: true },
  { id: "memberNumber", label: "MitgliedsNr.", sortable: true },
  { id: "standort", label: "Standort", sortable: true },
  { id: "degree", label: "Grad", sortable: true },
  { id: "age", label: "Alter", sortable: true },
  { id: "actions", label: "", sortable: false, width: 80 },
];

export default function TestingAttendeesList() {
  const { testingId } = useParams();
  const navigate = useNavigate();
  const {
    data: event,
    isFetching,
    refetch,
  } = useGetEventQuery(testingId, { skip: !testingId });
  const [removeAttendees, { isLoading: isRemoving }] =
    useRemoveAttendeesMutation();
  const [graduateAttendees, { isLoading: isGraduating }] =
    useGraduateAttendeesMutation();

  const [orderBy, setOrderBy] = useState("lastName");
  const [order, setOrder] = useState("asc"); // 'asc' | 'desc'

  const rows = useMemo(() => {
    const list = Array.isArray(event?.hasAttendees) ? event.hasAttendees : [];
    return list.map((a, idx) => ({
      _id: a?._id || a?.id || String(idx),
      index: idx + 1,
      firstName: a?.firstName || "",
      lastName: a?.lastName || "",
      memberNumber: a?.memberNumber || "",
      standort: getStandortName(a),
      degree: a?.degree ?? "",
      degreeLabel: gradLabel(a?.degree),
      age: getAge(a),
      raw: a,
    }));
  }, [event]);

  const sorted = useMemo(() => {
    if (!orderBy || !["asc", "desc"].includes(order)) return rows;
    const dir = order === "asc" ? 1 : -1;

    return [...rows].sort((a, b) => {
      const av = a[orderBy];
      const bv = b[orderBy];

      if (orderBy === "degree" || orderBy === "age") {
        const an = Number(av) || 0;
        const bn = Number(bv) || 0;
        return (an - bn) * dir;
      }

      if (
        orderBy === "memberNumber" ||
        orderBy === "standort" ||
        orderBy === "firstName" ||
        orderBy === "lastName"
      ) {
        return (
          String(av).localeCompare(String(bv), undefined, { numeric: true }) *
          dir
        );
      }

      return (
        String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir
      );
    });
  }, [rows, orderBy, order]);

  const handleSort = (id) => {
    if (orderBy === id) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(id);
      setOrder("asc");
    }
  };

  const handleRemove = async (memberId) => {
    if (!testingId || !memberId) return;
    const ok = window.confirm("Teilnehmer wirklich entfernen?");
    if (!ok) return;
    try {
      await removeAttendees({ id: testingId, memberIds: [memberId] }).unwrap();
      await refetch();
    } catch (e) {
      // TODO: toast/snackbar
    }
  };

  const handleGraduate = async () => {
    if (!testingId) return;
    if (!event?.hasAttendees?.length) {
      alert("Keine Teilnehmer zum Graduieren.");
      return;
    }
    const dateStr = event?.eventDateBegin
      ? dayjs(event.eventDateBegin).format("DD.MM.YYYY")
      : "heute";
    const ok = window.confirm(
      `Alle ${event.hasAttendees.length} Teilnehmer zum ${dateStr} in '${
        event.locationName ?? ""
      }' graduieren?`
    );
    if (!ok) return;

    try {
      await graduateAttendees(testingId).unwrap();
      await refetch(); // will return with empty attendees
    } catch (e) {
      // TODO: toast/snackbar
    }
  };

  // ---------- Summaries ----------
  // Build "next degree" label counts from attendees: next = degree + 1
  const nextLabelCounts = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const nextIdx = (Number(r.degree) || 0) + 1;
      const next = gradArray[nextIdx]?.label;
      if (!next) return;
      map.set(next, (map.get(next) || 0) + 1);
    });
    return map;
  }, [rows]);

  // Ordered labels for display
  const allLabels = useMemo(() => gradArray.map((g) => g.label), []);
  // 9.Kup → 1.Kup (exclude 10.Kup)
  const kupLabels = useMemo(
    () =>
      allLabels.filter(
        (lab) => /Kup/.test(lab) && !/^10\s*\.?\s*Kup/i.test(lab)
      ),
    [allLabels]
  );
  // Poom → 6.Dan
  const danLabels = useMemo(
    () =>
      allLabels.filter(
        (lab) => /Poom/i.test(lab) || /^\s*[1-6]\s*\.?\s*Dan/i.test(lab)
      ),
    [allLabels]
  );

  // Pair Kup & Dan rows into a single 4-col table
  const summaryRows = useMemo(() => {
    const maxLen = Math.max(kupLabels.length, danLabels.length);
    const rows = [];
    for (let i = 0; i < maxLen; i++) {
      const kl = kupLabels[i] || "";
      const kc = kl ? nextLabelCounts.get(kl) || 0 : "";
      const dl = danLabels[i] || "";
      const dc = dl ? nextLabelCounts.get(dl) || 0 : "";
      rows.push({ kl, kc, dl, dc });
    }
    return rows;
  }, [kupLabels, danLabels, nextLabelCounts]);

  // Age group counts by memberType (Kind, Jugend, Erwachsen)
  const ageGroupCounts = useMemo(() => {
    const res = { Kind: 0, Jugend: 0, Erwachsen: 0 };
    rows.forEach((r) => {
      const t = r?.raw?.memberType;
      if (t === "Kind") res.Kind += 1;
      else if (t === "Jugend") res.Jugend += 1;
      else if (t === "Erwachsen") res.Erwachsen += 1;
    });
    return res;
  }, [rows]);

  // ---------- NEW: Boards summary ----------
  const boardsSummary = useMemo(() => {
    const result = { kinder: 0, damen: 0, herren: 0 };

    const boardsNeededForNext = (label) => {
      if (!label) return 0;
      if (/Poom/i.test(label) || /Dan/i.test(label)) return 2;
      if (/Kup/i.test(label)) {
        const n = parseInt(label, 10);
        if (isNaN(n)) return 0;
        // 9..5 Kup -> 1 board; 4..1 Kup -> 2 boards
        return n >= 5 ? 1 : 2;
      }
      return 0;
    };

    rows.forEach((r) => {
      const nextIdx = (Number(r.degree) || 0) + 1;
      const nextLabel = gradArray[nextIdx]?.label;
      const boards = boardsNeededForNext(nextLabel);
      if (boards <= 0) return;

      const t = r?.raw?.memberType;
      const title = (r?.raw?.title || "").trim();

      if (t === "Kind") {
        result.kinder += boards;
      } else if (t === "Jugend") {
        result.damen += boards; // Teenagers grouped with Damen
      } else if (t === "Erwachsen") {
        if (title === "Frau") result.damen += boards;
        else result.herren += boards;
      }
    });

    return {
      ...result,
      total: result.kinder + result.damen + result.herren,
    };
  }, [rows]);

  return (
    <Paper variant="outlined">
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">
            {event?.eventName ?? "—"}{" "}
            {event?.eventDateBegin
              ? "· " + dayjs(event.eventDateBegin).format("DD.MM.YYYY HH:mm")
              : ""}
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => navigate("..")}
              startIcon={<Icon icon="mdi:arrow-left" />}
            >
              Zurück
            </Button>
            <TestingAttendeesPrintButton event={event} attendees={sorted} />
            <Button
              variant="contained"
              color="success"
              onClick={handleGraduate}
              disabled={
                isFetching || isGraduating || !(event?.hasAttendees?.length > 0)
              }
              startIcon={<Icon icon="mdi:school" />}
            >
              Graduieren
            </Button>
          </Stack>
        </Stack>

        <TableContainer sx={{ maxHeight: "70vh" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {cols.map((c) => (
                  <TableCell
                    key={c.id}
                    style={c.width ? { width: c.width } : undefined}
                  >
                    {c.sortable ? (
                      <TableSortLabel
                        active={orderBy === c.id}
                        direction={orderBy === c.id ? order : "asc"}
                        onClick={() => handleSort(c.id)}
                      >
                        {c.label}
                      </TableSortLabel>
                    ) : (
                      c.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!isFetching && sorted.length === 0 && (
                <TableRow>
                  <TableCell colSpan={cols.length}>
                    <Typography
                      variant="body2"
                      sx={{ py: 2, color: "text.secondary" }}
                    >
                      Keine Teilnehmer vorhanden.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {sorted.map((r, idx) => (
                <TableRow key={r._id + "-row"}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{r.firstName}</TableCell>
                  <TableCell>{r.lastName}</TableCell>
                  <TableCell>{r.memberNumber}</TableCell>
                  <TableCell>{r.standort}</TableCell>
                  <TableCell>{r.degreeLabel}</TableCell>
                  <TableCell>{r.age}</TableCell>
                  <TableCell>
                    <Tooltip title="Entfernen">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={isRemoving || isGraduating}
                          onClick={() => handleRemove(r._id)}
                        >
                          <Icon icon="mdi:trash-can-outline" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ---- Summaries ---- */}
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Gürtelbedarf (nächster Grad)
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small" aria-label="belt-summary">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Kup</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Anzahl</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Poom / Dan</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Anzahl</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryRows.map((r, i) => (
                  <TableRow key={`sum-${i}`}>
                    <TableCell>{r.kl || ""}</TableCell>
                    <TableCell>{r.kc !== "" ? r.kc : ""}</TableCell>
                    <TableCell>{r.dl || ""}</TableCell>
                    <TableCell>{r.dc !== "" ? r.dc : ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Teilnehmer nach Altersgruppe
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
            <Chip label={`Kinder: ${ageGroupCounts.Kind}`} color="info" />
            <Chip
              label={`Jugendliche: ${ageGroupCounts.Jugend}`}
              color="info"
            />
            <Chip
              label={`Erwachsene: ${ageGroupCounts.Erwachsen}`}
              color="info"
            />
            <Chip
              label={`Gesamt: ${
                ageGroupCounts.Kind +
                ageGroupCounts.Jugend +
                ageGroupCounts.Erwachsen
              }`}
              color="secondary"
            />
          </Stack>

          {/* NEW: Boards summary */}
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Bretterbedarf
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small" aria-label="boards-summary">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Kinderbretter</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Damenbretter</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Herrenbretter</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Gesamt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{boardsSummary.kinder}</TableCell>
                  <TableCell>{boardsSummary.damen}</TableCell>
                  <TableCell>{boardsSummary.herren}</TableCell>
                  <TableCell>{boardsSummary.total}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Bottom actions */}
        <Stack alignItems="end" direction="row" spacing={1} sx={{ pt: 2 }}>
          <TestingAttendeesPrintButton event={event} attendees={sorted} />
          <Button
            variant="contained"
            color="success"
            onClick={handleGraduate}
            disabled={
              isFetching || isGraduating || !(event?.hasAttendees?.length > 0)
            }
            startIcon={<Icon icon="mdi:school" />}
          >
            Graduieren
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}
